// PRD-1387 — picking the CSV review job out of the /jobs payload.
//
// This used to be an inline `find` in App.js keyed on the v1 graph label:
//
//   jobs.find((j) => j.pipeline === 'csv_structure_pipeline' && j.status === 'awaiting_input')
//
// `pipeline` is a v1 name. On v2 the column is NULL on pre-created jobs and
// `unstructured_file_pipeline` on the rest, so that predicate matched nothing —
// the fallback was dead code the moment v2 shipped. PRD-1386 retires the column
// outright, which is why the read has to go rather than be re-spelled with a v2
// name.
//
// `status === 'awaiting_input'` is the real dependency and survives the
// retirement: the backend sets it whenever a job is parked for human input.
// It is not sufficient on its own, though — the id the review endpoints take is
// the CSV ingestion job's, which the /jobs endpoint attaches as `csv_job_id`.
// So a job qualifies only when it is parked AND carries a `csv_job_id`.
//
// Requiring the csv_job_id is what keeps this from resting on an assumption.
// Today only the CSV path parks a job this way (traced 2026-09-25: the sole
// writer is csv-ingestion-service's _handle_hitl_suspend, which creates the
// csv_ingestion_jobs row in the same step), but that is a convention —
// resapi's internal status endpoint accepts `awaiting_input` from any caller.
// If another flow ever learns to park a job, its job carries no csv_job_id and
// is ignored here, instead of being opened in the CSV review modal.
//
// Why there is no fallback to the job's own `id`: /jobs leaves csv_job_id off
// a parked job only when no suspended csv_ingestion_jobs row points back at it
// through suspended_state.resapi_job_id. The review endpoints, handed that same
// job id, resolve it with the same lookup — so it 404s every time. Returning it
// would open the modal on an id already known to fail; null falls through to
// the caller's "re-index to review" message instead.

/**
 * The job id to open the CSV review modal against, from the per-dataset /jobs
 * payload.
 *
 * Only reached when the dataset rollup carries no `csv_job_id` — i.e. the brief
 * race between a job suspending and the rollup catching up.
 *
 * Returns the `csv_job_id` of the first job that is both parked for input
 * and carries one. A parked job without a csv_job_id is skipped, never
 * returned by its own id — see the header for why that id cannot work.
 *
 * @param {Array<object>} jobs - response from apiService.getDatasetJobs
 * @returns {string|null} csv job id, or null when no parked job carries one
 */
export function selectAwaitingInputJobId(jobs) {
  if (!Array.isArray(jobs)) return null;
  const job = jobs.find((j) => j?.status === 'awaiting_input' && j.csv_job_id);
  return job ? job.csv_job_id : null;
}
